import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Modal from '../components/UI/Modal';
import LeadSourceForm from '../components/Forms/LeadSourceForm';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit, FiTrash2, FiTarget, FiToggleLeft, FiToggleRight } = FiIcons;

const LeadSources = () => {
  const { isAdmin, isManager } = useAuth();
  const [leadSources, setLeadSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);

  useEffect(() => {
    loadLeadSources();
  }, []);

  const loadLeadSources = async () => {
    try {
      const { data, error } = await supabase
        .from('lead_sources_crm_2025')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedSources = data?.map(source => ({
        id: source.id,
        name: source.name,
        description: source.description,
        isActive: source.is_active,
        createdAt: source.created_at,
        updatedAt: source.updated_at
      })) || [];

      setLeadSources(formattedSources);
    } catch (error) {
      console.error('Error loading lead sources:', error);
      toast.error('Failed to load lead sources');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSource = () => {
    setEditingSource(null);
    setIsModalOpen(true);
  };

  const handleEditSource = (source) => {
    setEditingSource(source);
    setIsModalOpen(true);
  };

  const handleSubmit = async (data) => {
    try {
      const sourceData = {
        name: data.name,
        description: data.description || null,
        is_active: data.isActive !== false
      };

      if (editingSource) {
        const { error } = await supabase
          .from('lead_sources_crm_2025')
          .update({ ...sourceData, updated_at: new Date().toISOString() })
          .eq('id', editingSource.id);

        if (error) throw error;
        toast.success('Lead source updated successfully');
      } else {
        const { error } = await supabase
          .from('lead_sources_crm_2025')
          .insert([sourceData]);

        if (error) throw error;
        toast.success('Lead source added successfully');
      }

      loadLeadSources();
      setIsModalOpen(false);
      setEditingSource(null);
    } catch (error) {
      console.error('Error saving lead source:', error);
      if (error.code === '23505') {
        toast.error('A lead source with this name already exists');
      } else {
        toast.error('Failed to save lead source');
      }
    }
  };

  const handleDeleteSource = async (sourceId) => {
    if (window.confirm('Are you sure you want to delete this lead source? This may affect existing leads.')) {
      try {
        const { error } = await supabase
          .from('lead_sources_crm_2025')
          .delete()
          .eq('id', sourceId);

        if (error) throw error;

        toast.success('Lead source deleted successfully');
        loadLeadSources();
      } catch (error) {
        console.error('Error deleting lead source:', error);
        toast.error('Failed to delete lead source');
      }
    }
  };

  const handleToggleStatus = async (sourceId, currentStatus) => {
    try {
      const { error } = await supabase
        .from('lead_sources_crm_2025')
        .update({ 
          is_active: !currentStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', sourceId);

      if (error) throw error;

      toast.success(`Lead source ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      loadLeadSources();
    } catch (error) {
      console.error('Error updating lead source status:', error);
      toast.error('Failed to update lead source status');
    }
  };

  if (!isAdmin && !isManager) {
    return (
      <div className="space-y-6">
        <Card className="p-12 text-center">
          <SafeIcon icon={FiTarget} className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-500">You don't have permission to manage lead sources.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading lead sources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Lead Sources</h1>
        <Button onClick={handleAddSource}>
          <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
          Add Source
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50">
              <SafeIcon icon={FiTarget} className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Sources</p>
              <p className="text-2xl font-bold text-gray-900">{leadSources.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50">
              <SafeIcon icon={FiToggleRight} className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Sources</p>
              <p className="text-2xl font-bold text-gray-900">
                {leadSources.filter(s => s.isActive).length}
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-gray-50">
              <SafeIcon icon={FiToggleLeft} className="h-6 w-6 text-gray-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Inactive Sources</p>
              <p className="text-2xl font-bold text-gray-900">
                {leadSources.filter(s => !s.isActive).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Lead Sources List */}
      {leadSources.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiTarget} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No lead sources found</h3>
          <p className="text-gray-500 mb-4">Add lead sources to categorize where your leads come from.</p>
          <Button onClick={handleAddSource}>
            <SafeIcon icon={FiPlus} className="h-4 w-4 mr-2" />
            Add Source
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {leadSources.map((source, index) => (
            <motion.div
              key={source.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={`p-6 hover:shadow-lg transition-shadow ${!source.isActive ? 'opacity-60' : ''}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {source.name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${source.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {source.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => handleEditSource(source)}
                      className="text-gray-400 hover:text-primary-600 transition-colors"
                      title="Edit Source"
                    >
                      <SafeIcon icon={FiEdit} className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(source.id, source.isActive)}
                      className={`transition-colors ${source.isActive ? 'text-gray-400 hover:text-orange-600' : 'text-gray-400 hover:text-green-600'}`}
                      title={source.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <SafeIcon icon={source.isActive ? FiToggleRight : FiToggleLeft} className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSource(source.id)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                      title="Delete Source"
                    >
                      <SafeIcon icon={FiTrash2} className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {source.description && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">{source.description}</p>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Created {format(new Date(source.createdAt), 'MMM dd, yyyy')}
                  </p>
                  {source.updatedAt && source.updatedAt !== source.createdAt && (
                    <p className="text-xs text-gray-500">
                      Updated {format(new Date(source.updatedAt), 'MMM dd, yyyy')}
                    </p>
                  )}
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingSource(null);
        }}
        title={editingSource ? 'Edit Lead Source' : 'Add New Lead Source'}
        size="md"
      >
        <LeadSourceForm
          leadSource={editingSource}
          onSubmit={handleSubmit}
          onCancel={() => {
            setIsModalOpen(false);
            setEditingSource(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default LeadSources;